
import { useCallback } from "react";
import { supabaseWithAuth } from "@/utils/supabaseWrapper";
import { logger } from "@/utils/productionLogger";

export type OrgMember = {
  id: string;
  organization_id: string | null;
  user_id: string | null;
  role: "owner" | "admin" | "member";
  status: "invited" | "active" | "suspended";
  invited_by: string | null;
  invited_at: string | null;
  activated_at: string | null;
  created_at: string | null;
  profile?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    company: string;
    position: string;
  } | null;
};

export const useOrganizationManagement = () => {
  /**
   * Invite un membre via l’edge function (vérifie permissions, quotas, email pro et envoie l’email)
   */
  const inviteMember = useCallback(async (
    organizationId: string, 
    email: string, 
    role: "admin" | "member" = "member",
    firstName?: string,
    lastName?: string
  ) => {
    try {
      const { data, error } = await supabaseWithAuth.client.functions.invoke("send-organization-invitation", {
        body: { 
          organization_id: organizationId, 
          email, 
          role,
          first_name: firstName,
          last_name: lastName
        },
      });

      if (error) {
        console.error("inviteMember error:", error);

        // Gestion spécifique des erreurs
        let userFriendlyMessage = error.message;

        if (error.message?.includes("MISSING_CONFIG") || error.message?.includes("INVALID_CONFIG")) {
          userFriendlyMessage = "Problème de configuration serveur. Contactez le support.";
        } else if (error.message?.includes("Quota de sièges atteint")) {
          userFriendlyMessage = "Quota de sièges atteint. Mettez à niveau votre abonnement.";
        } else if (error.message?.includes("email professionnelle")) {
          userFriendlyMessage = "Veuillez utiliser une adresse email professionnelle.";
        } else if (error.message?.includes("temporairement indisponible")) {
          userFriendlyMessage = "Service temporairement indisponible. Réessayez dans quelques minutes.";
        } else if (error.message?.includes("Configuration email incorrecte")) {
          userFriendlyMessage = "Problème de configuration email. Contactez l'administrateur.";
        }

        return {
          data: null,
          error: {
            ...error,
            userMessage: userFriendlyMessage
          }
        };
      }

      return { data, error: null };
    } catch (networkError) {
      console.error("Network error in inviteMember:", networkError);
      return {
        data: null,
        error: {
          message: "Erreur de connexion. Vérifiez votre connexion internet.",
          userMessage: "Erreur de connexion. Vérifiez votre connexion internet."
        }
      };
    }
  }, []);

  /**
   * Récupère les membres d’une organisation et enrichit avec les profils
   */
  const fetchMembers = useCallback(async (organizationId: string) => {
    const { data: members, error } = await supabaseWithAuth.executeWithRetry(async () => {
      return supabaseWithAuth
        .from("organization_members")
        .select("*")
        .eq("organization_id", organizationId);
    }, 'organization_management.fetch_members');

    if (error) {
      console.error("fetchMembers error:", error);
      return { data: [] as OrgMember[], error };
    }

    const userIds = (members || []).map((m: any) => m.user_id).filter((id): id is string => !!id);
    let profilesById: Record<string, OrgMember["profile"]> = {};

    if (userIds.length > 0) {
      const { data: profiles } = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from("profiles")
          .select("id, email, first_name, last_name, company, position")
          .in("id", userIds);
      }, 'organization_management.fetch_profiles');

      profilesById = (profiles || []).reduce((acc: Record<string, OrgMember["profile"]>, p: any) => {
        if (p?.id) {
          acc[p.id] = p;
        }
        return acc;
      }, {});
    }

     
    const withProfiles: OrgMember[] = (members || []).map((m: any) => {
      return {
        id: m.id,
        organization_id: m.organization_id,
        user_id: m.user_id,
        role: m.role,
        status: m.status,
        invited_by: m.invited_by,
        invited_at: m.invited_at,
        activated_at: m.activated_at,
        created_at: m.created_at,
        profile: m.user_id ? profilesById[m.user_id] ?? null : null,
      };
    });

    return { data: withProfiles, error: null };
  }, []);

  /**
   * Met à jour le rôle d’un membre (owner/admin requis côté RLS)
   */
  const updateMemberRole = useCallback(async (organizationId: string, userId: string, role: "admin" | "member") => {
    const { error } = await supabaseWithAuth.executeWithRetry(async () => {
      return supabaseWithAuth
        .from("organization_members")
        .update({ role })
        .eq("organization_id", organizationId)
        .eq("user_id", userId);
    }, 'organization_management.update_member_role');

    if (error) {
      console.error("updateMemberRole error:", error);
      return { error };
    }
    return { error: null };
  }, []);

  /**
   * Récupère les invitations en attente pour une organisation
   */
  const fetchPendingInvitations = useCallback(async (organizationId: string) => {
    const { data: invitations, error } = await supabaseWithAuth.executeWithRetry(async () => {
      return supabaseWithAuth
        .from("organization_invitations")
        .select("*")
        .eq("organization_id", organizationId)
        .is("used_at", null)
        .order("created_at", { ascending: false });
    }, 'organization_management.fetch_pending_invitations');

    if (error) {
      console.error("fetchPendingInvitations error:", error);
      return { data: [], error };
    }

    return { data: invitations || [], error: null };
  }, []);

  /**
   * Annule une invitation en attente via l'edge function
   */
  const cancelInvitation = useCallback(async (invitationId: string) => {
    logger.debug("Suppression de l'invitation via Edge Function", { invitationId });

    const { data, error } = await supabaseWithAuth.client.functions.invoke("cancel-organization-invitation", {
      body: { invitation_id: invitationId },
    });

    logger.debug("Résultat suppression Edge Function", { hasData: !!data, hasError: !!error });

    if (error) {
      console.error("cancelInvitation error:", error);
      return { error };
    }

    logger.info("Invitation supprimée avec succès via Edge Function");
    return { error: null };
  }, []);

  /**
   * Retire un membre (owner/admin requis côté RLS)
   */
  const removeMember = useCallback(async (organizationId: string, userId: string) => {
    const { error } = await supabaseWithAuth.executeWithRetry(async () => {
      return supabaseWithAuth
        .from("organization_members")
        .delete()
        .eq("organization_id", organizationId)
        .eq("user_id", userId);
    }, 'organization_management.remove_member');

    if (error) {
      console.error("removeMember error:", error);
      return { error };
    }
    return { error: null };
  }, []);

  return {
    inviteMember,
    fetchMembers,
    fetchPendingInvitations,
    updateMemberRole,
    removeMember,
    cancelInvitation,
  };
};
